import { Component, Injector, ViewChild, inject, signal } from "@angular/core";
import { RowClickedEvent } from "ag-grid-community";
import { from } from "rxjs";

import {
    EntityManagerService,
    RIGHT_SIDENAV_PORTAL_DATA_TOKEN,
    RightSidenavService,
    ToolbarEventsService
} from "src/app/core";

import { GridCellExportFormatterService, GridCellFormatterService, GridClickableButtonComponent, GridFixedButtons } from "src/app/shared/components";
import { GridService } from "src/app/shared/components/grid/grid.service";
import { DataExport, SaleItemsDeletedFe, TableData } from "src/app/shared/model/model";
import { headersTranslate,} from 'src/app/shared/components/grid';
import { restManager } from "app/ajs-upgraded-providers";
import { HistorySaleDetailComponent } from "../history-sales/components/history-sale-detail/history-sale-detail.component";
import { ComponentPortal } from "@angular/cdk/portal";
import { GridClientSideComponent } from "src/app/shared/components/grid-client-side/grid-client-side.component";
import { SaleItemsDeleted } from "tilby-models";
import { subscribeInComponent } from "src/app/shared/angular-utils";
import { GridDownloadDialogService } from "src/app/dialogs";
import { autobind } from "src/app/models/decorators.model";
import { GeneralInputButton } from "@tilby/tilby-ui-lib/models";
import { ToolbarLayoutService } from "src/app/shared/services/toolbar-layout.service";

const productsDeleteOptions = {
    sort_type: "desc",
    sort_col: 'date'
}

@Component({
	standalone: false,
    selector: 'history-products-deleted',
    templateUrl: './history-products-deleted.component.html',
    styleUrls: ['./history-products-deleted.component.scss'],
})
export class HistoryProductsDeletedComponent {
    @ViewChild("appHistoryProductsDeletedComponent", { static: true }) gridRef!: GridClientSideComponent;

    private readonly gridService = inject(GridService);
    private readonly restManagerService = inject(restManager);
    private readonly entityManager = inject(EntityManagerService);
    private readonly toolbarEventsService = inject(ToolbarEventsService);
    private readonly gridCellFormatterService = inject(GridCellFormatterService);
    private readonly gridCellExportFormatterService = inject(GridCellExportFormatterService);
    private readonly gridDownloadDialogService = inject(GridDownloadDialogService);
    private readonly rightSidenavService = inject(RightSidenavService<HistorySaleDetailComponent>);
    private readonly toolbarLayoutService = inject(ToolbarLayoutService);

    saleItemsDeleted: SaleItemsDeletedFe[] = [];
    exportFormatterOptions?: (...any: any[])=> any;
    fixedButtons: GridFixedButtons;

    async mapFields(a: any, b: any): Promise<any> {
        const response = await this.restManagerService.getList(a, b);

        for(let i = 0; i < response.length; i++) {
            response[i].id = i+1;
        }

        return new Promise((resolve) => 
            {
                resolve({
                    page: 0,
                    pages: 1,
                    per_page: response.length,
                    results: response,
                    total: response.length
                })
            }
        )
    }

    tableData: TableData[] = [
        {
            rowData$: from(this.entityManager.saleItemsDeleted.fetchCollectionOnline(productsDeleteOptions).then(res => res && Array.isArray(res) ? this.mapSaleItemsDeleted(res): [])),
            dataType: new SaleItemsDeletedFe(),
            headersTranslate: headersTranslate.history_products_deleted,
            columnsFormatter: this.gridCellFormatterService.history_products_deleted
        }
    ];

    mapSaleItemsDeleted(data: SaleItemsDeleted[]) {
        const result: any[] = [];

        data?.forEach((element, idx) => {
            result.push({
                id: idx,
                sale_item_deleted_at: element.sale_item_deleted_at ? new Date(element.sale_item_deleted_at) : "",
                lastupdate_at: element.sale_item_details?.lastupdate_at ? new Date(element.sale_item_details?.lastupdate_at) : "",
                sale_id: element.sale_id || '',
                sale_name: element.sale_name || '',
                item_id: element.sale_item_details?.item_id || '',
                name: element.sale_item_details?.name || '',
                sku: element.sale_item_details?.sku || '',
                category_name: element.sale_item_details?.category_name || '',
                department_name:  element.sale_item_details?.department?.name || '',
                vat_perc: element.sale_item_details?.vat_perc || '',
                price: element.sale_item_details?.price || '',
                cost: element.sale_item_details?.cost || '',
                operator_name: element.operator_name || '',
                room_name: element.room_name || '',
                table_name: element.table_name || '',
                covers: element.covers || '',
                order_type: element.order_type || '',
                quantity_difference: element.neg_quantity_difference || 0,
                revenues: (element.sale_item_details?.price && element?.neg_quantity_difference) ? element.sale_item_details?.price * element?.neg_quantity_difference : 0,
            });
        });

        return result;
    }

    constructor() {
        this.fixedButtons = {
            checkbox: { visible: true, lockPosition: 'left' },
            customButton: { visible: false, cellRenderer: GridClickableButtonComponent, lockPosition: 'right' }
        };

        subscribeInComponent(this.gridService.history_products_deleted$.data, data => {
            if(this.gridRef.agGrid.gridOptions) {
                this.gridRef.agGrid.gridOptions.rowClass = 'ag-row-background__unselected';
            }

            this.saleItemsDeleted = data;
        });

        subscribeInComponent(this.toolbarEventsService.events, event =>{
            switch(event.type) {
                case 'toolbar.search':
                    this.gridRef.onFilterTextBoxChanged(event.search);
                    break;
                case 'grid.open_tool_panel':
                    this.gridRef.openToolPanel(event.panel);
                    break;
            }
        });
    }

    ngOnInit(): void {
        this.createToolbarButtons();
        this.setModuleTitle();

        this.toolbarEventsService.searchBarValue$.next('');
        this.toolbarEventsService.searchBarIcon$.next('tune');
        this.toolbarEventsService.searchBarIconFilled$.next('tune');

        this.toolbarEventsService.searchBarAction$.next({ type: 'grid.open_tool_panel', panel: 'filters' });
        this.toolbarEventsService.searchBarActionFilled$.next({ type: 'grid.open_tool_panel', panel: 'filters' });

        this.exportFormatterOptions = this.gridCellExportFormatterService.historySaleItemsDeleteFormatter;
    }

    createToolbarButtons() {
        const { allButtons, fixedButtons } = this.getToolbarConfig();
        this.toolbarLayoutService.updateToolbarLayout(allButtons, fixedButtons);
    }

    private getToolbarConfig() {
        const trueSignal = signal(true);
        
        const allButtons: GeneralInputButton[] = [
            {
                isIt: () => Object.keys(this.gridRef?.gridApi?.getFilterModel() || {}).length > 0,
                name: "clear_filters",
                icon: signal('filter_alt_off'),
                label: signal('TOPBAR.ACTIONS.CLEAR_FILTERS'),
                click: () => this.clearGridFilters()
            }
        ]
        
        const fixedButtons: GeneralInputButton[] = [
            {
                isIt: trueSignal,
                name: "export",
                icon: () => "download",
                label: () => "TOPBAR.ACTIONS.EXPORT",
                click: () => this.triggerExport()
            }
        ]

        return { allButtons, fixedButtons };
    }

    onFilterChanged(): void {
        this.createToolbarButtons();
    }

    async triggerExport() {
        this.gridRef.setExportPreferenceFromVisibleColumns();

        const res = await this.gridDownloadDialogService.openDialog('history_products_deleted', {
            data: this.tableData.map(tableData => ({
                ...tableData,
                sortFilterQuery: this.gridRef.getSortFilterQuery(),
                countSelectedRows: this.gridRef.getSelectedCount() || this.gridRef.gridApi?.getDisplayedRowCount()
            }))
        });

        if (res) {
            this.onExport(res);
        }
    }

    @autobind
    onExport(dataExport: DataExport) {
        const rowSelected = !!this.gridRef.getSelectedCount();
        this.gridRef.clientSideExport(dataExport, rowSelected);
    }

    setModuleTitle() {
        this.toolbarEventsService.moduleTitle.next("PRODUCTS-DELETED");
    }

    clearGridFilters() {
        this.gridRef.gridApi?.setFilterModel(null);
        this.gridRef.gridApi?.onFilterChanged();
    }

    async rowClicked({data, node}: RowClickedEvent) {
        const sale_id = data.sale_id;
        const sale = await this.restManagerService.getOne(`sales/${sale_id}`);

        if(sale.length === 0){
            this.rightSidenavService.isOpen = false;
            return;
        }

        const portalInjector = Injector.create({
            providers: [{
                provide: RIGHT_SIDENAV_PORTAL_DATA_TOKEN,
                useValue: {sale, gridRef: this.gridRef}
            }],
        });
        this.rightSidenavService.componentPortal$.next(new ComponentPortal(HistorySaleDetailComponent, null, portalInjector));
        this.rightSidenavService.isOpen = true;
    }
}